import React, { useEffect } from 'react';
import '../../App.css';
import CSS from 'csstype';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import closeIcon from '../../icons/close.254.png';
import searchLocationIcon from '../../icons/search-location.512.png';
import searchCatalogIcon from '../../icons/search-catalog.512.png';
import searchCatalogDisabledIcon from '../../icons/search-catalog-bw.512.png';

// classes
import SearchCatalogResults from './search-catalog-results';
import SearchCatalogForm from './search-catalog-form';
import SelectedItemsTab from './selected-items-tab';
import SelectedItemsPanel from './selected-items-panel';
import ToolSmall from '../../tools/tool-small';
import ToolButton from '../../tools/tool-button';
import SidePanel from '../../tools/side-panel';
import { HideDropdownMenu } from '../../utils/functions';
import { Aladin } from '../interactive-sessions/aladin';
import { moveAladinView } from '../interactive-sessions/aladin';
import { useTranslation } from 'react-i18next';
import Scrollbox from '../../tools/scrollbox';
import { ContextMenu } from '../menus/context-menu';

// types
import { JobType, ComputeServiceType, Site } from '../../utils/types';

// enum
import { SidePanelPosition } from '../../tools/side-panel';

// functions
import { APIPrefix, ContainerHook, siteName } from '../../utils/functions';

import { Col, Row } from 'react-bootstrap';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  fnD_buildRucioLocations,
  fnD_getAvailableLocations,
  fnD_getStorageLocationSite,
} from '../../app/data-actions';
import {
  addSearchResults,
  closeSearchResultsTab,
  setAladin,
  setAladinMaximised,
  setContextMenuRefreshStageData,
  setDropdownWidth,
  setFirstTab,
  setHighlightedID,
  setLoadingAladin,
  setMaxTabs,
  setSearchCatalogTab,
  setSearchResults,
  setSelectedIDs,
  setTabDropdownDisplayed,
  setTabWidth,
} from './search-catalog-slice';
import {
  buildQuery,
  getDataProductTypesInResults,
  getTapData,
} from './search-catalog-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_CONTAINERS,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

import { RucioLocationsType } from '../../utils/types';

// types relating to events.
import {
  Event,
  EventTarget,
  EventType,
  PositionWithID,
  PositionAndFOV,
} from '../../utils/events';

// types specifically for this component.
import { SelectedRowsType, LocationsType, QueryParameters } from './search-catalog-types';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCatalog(props: { dataManagementJobs: JobType[] }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL STATE VARIABLES:
  const sG_SiteCapabilitiesTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.siteCapabilitiesTokenObtained,
  );
  const sG_Project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );

  // DATA STATE VARIABLES:
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  // rucio locations of selected data.
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  ).filter((filter) => filter.catalog.indexOf('search-catalog') > -1);

  // LOCAL STATE VARIABLES:

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedRows,
  );

  // footprint has been selected.
  const sSelectedIDs: { id: string; ra: number; dec: number }[] = useAppSelector(
    (state) => state.searchCatalog.selectedIDs,
  );

  // footprint is highlighted.
  const sHighlightedID: string = useAppSelector(
    (state) => state.searchCatalog.highlightedID,
  );

  // search catalog tool:
  const sSearchCatalogTab: string = useAppSelector(
    (state) => state.searchCatalog.searchCatalogTab,
  );
  const sSearchResults: QueryParameters[] = useAppSelector(
    (state) => state.searchCatalog.searchResults,
  );
  const sTabDropdownDisplayed: boolean = useAppSelector(
    (state) => state.searchCatalog.tabDropdownDisplayed,
  );
  const sPageAllTabs: { id: string; page: number }[] = useAppSelector(
    (state) => state.searchCatalog.page,
  );
  const sDPTFilterAllTabs: { id: string; dptFilter: string }[] = useAppSelector(
    (state) => state.searchCatalog.dptFilter,
  );
  const sDisplayedPageAllTabs: {
    id: string;
    dptFilter: string;
    page: number;
    pageSize: number;
  }[] = useAppSelector((state) => state.searchCatalog.displayedPage);
  const sPageSizeAllTabs: { id: string; pageSize: number }[] = useAppSelector(
    (state) => state.searchCatalog.pageSize,
  );
  const sContextMenuRefreshStageData: number = useAppSelector(
    (state) => state.searchCatalog.contextMenus.refreshStageData,
  );

  // EXTRACT LOCAL STATE VARIABLES:
  // ------------------------------

  // manage data-product types.
  const sDPTFilter: string | undefined =
    sDPTFilterAllTabs.find((item) => item.id === sSearchCatalogTab)?.dptFilter ??
    undefined;

  // manage paging.
  const sPage: number | undefined =
    sPageAllTabs.find((item) => item.id === sSearchCatalogTab)?.page ?? undefined;
  const sPageSize: number | undefined =
    sPageSizeAllTabs.find((item) => item.id === sSearchCatalogTab)?.pageSize ?? undefined;
  const sDisplayedPage:
    | { id: string; dptFilter: string; page: number; pageSize: number }
    | undefined =
    sDisplayedPageAllTabs.find((item) => item.id === sSearchCatalogTab) ?? undefined;

  // loading Aladin.
  const sLoadingAladin: boolean = useAppSelector(
    (state) => state.searchCatalog.loadingAladin,
  );

  // current Aladin instance.
  const sAladin: any | null = useAppSelector((state) => state.searchCatalog.aladin);
  const sAladinMaximised: boolean = useAppSelector(
    (state) => state.searchCatalog.aladinMaximised,
  );

  //	-------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	-------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({
    container: CONTEXT_CONTAINERS.SEARCH_CATALOG_WHOLE_WINDOW,
  });

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	function that adds a new search results tab when the user clicks on
  //	search on the search parameters form.
  //
  //	-------------------------------------------------

  function addSearchResultsTab(args: { query: QueryParameters }) {
    //
    //  use the next available ID to number the search results.
    dispatch(addSearchResults(args.query));

    // build the query (the id should have been populated by addSearchResults).
    dispatch(buildQuery({ query: args.query }));

    // Set the current tab to the new results tab.
    dispatch(setSearchCatalogTab(null));

    // kick off the search.
    dispatch(getTapData({ query: args.query }));

    // get a list of data-product types in this search.
    dispatch(getDataProductTypesInResults({ query: args.query }));

    // restart Aladin at this position, but only for cone searches.
    if (
      args.query.ra !== undefined &&
      args.query.dec !== undefined &&
      args.query.fov !== undefined &&
      args.query.adqlQuery === undefined
    ) {
      //
      //moveAladinView({
      //  aladin: sAladin,
      //  ra: args.query.ra,
      //  dec: args.query.dec,
      //  fov: args.query.fov,
      //  selectedIDs: args.query.obs_publisher_did,
      //  setSelectedIDs: setSelectedIDs,
      //  loadCatalog: true,
      //});

      // the zoomChanged event in Aladin is not triggered when we update the FOV using setFOV(). it's only triggered by a user zoom.
      // so in order to update the FOV in the Filter window we need to set it here.
      const posAndFOV: PositionAndFOV = {
        fov: args.query.fov,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.SEARCH_CATALOG_FORM,
        eventType: EventType.UPDATE_SEARCH_RA_DEC,
        parameters: posAndFOV,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }
    //
  } // addSearchResultsTab

  //	------------------------------------------------------------
  //
  //	Launches a notebook for the given file. We will use the
  //	optionally provided site preferentially, assuming that the
  //	data exists on this site.
  //
  //	------------------------------------------------------------

  async function launchService(args: {
    type: string;
    namespace: string;
    filename: string;
    site?: Site;
  }) {
    //
    let sites: Site[] = [];

    // get storage area IDs
    let storageAreas: {
      identifier: string;
      associated_storage_area_id: string;
      replicas: string[];
    }[] = await dispatch(
      fnD_getAvailableLocations({
        items: [
          {
            filename: args.namespace,
            namespace: args.filename,
          },
        ],
      }),
    ).unwrap();

    // loop through the rucio locations that were returned, adding each one to
    // the sites array.
    for (var locationID: number = 0; locationID < storageAreas.length; locationID++) {
      const site: Site = fnD_getStorageLocationSite({
        id: storageAreas[locationID].associated_storage_area_id,
      });
      sites.push(site);
      //
    }

    // if a preferred site has been provided, and is found in the list,
    // then shuffle it to the first element.
    if (args.site !== undefined) {
      let index: number = sites.findIndex(
        (item) => item.site_uuid === args.site?.site_uuid,
      );
      if (index > -1)
        for (var i: number = 0; i < sites.length - 1; i++)
          for (var j: number = i + 1; j < sites.length; j++)
            if (sites[j] < sites[i] || sites[j] === args.site) {
              var tmp: Site = sites[j];
              sites[j] = sites[i];
              sites[i] = tmp;
            }
    }

    // pick a site that has got a suitable service.
    var serviceLaunched: boolean = false;
    for (var i: number = 0; i < sites.length; i++) {
      //
      // find this site in the local-compute array.
      for (let j: number = 0; j < sD_computeServices.length; j++) {
        if (
          sites[i].site_uuid === sD_computeServices[j].site.site_uuid &&
          sD_computeServices[j].session_type.toUpperCase() === args.type.toUpperCase() &&
          serviceLaunched === false
        ) {
          //
          const service: ComputeServiceType = sD_computeServices[j];

          if (service.is_embedded === false || args.type.toUpperCase() !== 'JUPYTERHUB') {
            window.open(service.service_url, '_blank', 'noreferrer');
          } else {
            const newEvent: Event = {
              eventTarget: EventTarget.APP,
              eventType: EventType.LAUNCH_NOTEBOOK,
              parameters: service.service_url,
            };
            dispatch(
              handleEvent({
                event: newEvent,
              }),
            );
          }

          serviceLaunched = true;
          //
        }
      }
      //
    }
    //
  } // launchService

  //	------------------------------------------------------------
  //
  //	Handler for button onClick events.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // set which tab is selected on the Search Catalog form. If we clicked on a results tab, then store the index of the tab.
    if (button.name === 'btnSelectedItems') {
      dispatch(setSearchCatalogTab('selected-items'));
    }
    if (button.name === 'searchLocation') {
      //
      // get the current position and FOV.
      const position: Float64Array = sAladin.getRaDec();
      const fov: Float64Array = sAladin.getFov();

      const newQuery: QueryParameters = {
        id: '',
        ra: position[0],
        dec: position[1],
        fov: fov[0],
        obs_publisher_did: [],
      };
      addSearchResultsTab({ query: newQuery });
      //
    }

    // if we've chosen to search for the selected objects in the TAP catalog, then compute the average RA and DEC.
    if (button.name === 'searchSelectedObject' && sSelectedIDs.length > 0)
      viewDataFromAladinFootprint({ position: sSelectedIDs });

    if (button.name === 'minimiseAladin') {
      dispatch(setAladinMaximised(false));
    }
    if (button.name === 'maximiseAladin') {
      dispatch(setAladinMaximised(true));
    }

    // if we clicked on a tab, then change the currently selected tab.
    if (button.name.length > 10)
      if (button.name.slice(0, 10) === 'btnResults') {
        //
        const newTab: string = button.name.slice(10 - button.name.length);

        // update the currently-selected tab.
        if (newTab !== undefined) {
          dispatch(setSearchCatalogTab(newTab));
        }

        // find the ID of the selected tab within the array of results tabs.
        let index = sSearchResults.findIndex((element) => element.id === newTab);

        // if we found it then get the ra, dec, and FOV.
        if (index > -1) {
          //
          var ra: number | undefined = sSearchResults[index].ra;
          var dec: number | undefined = sSearchResults[index].dec;
          var fov: number | undefined = sSearchResults[index].fov;

          // reload Aladin at this position.
          if (ra !== undefined && dec !== undefined && fov !== undefined) {
            //
            //moveAladinView({
            //  aladin: sAladin,
            //  ra: ra,
            //  dec: dec,
            //  fov: fov,
            //  selectedIDs: [],
            //  setSelectedIDs: undefined,
            //  loadCatalog: true,
            //});

            // the zoomChanged event in Aladin is not triggered when we update the FOV using setFOV(). it's only triggered by a user zoom.
            // so in order to update the FOV in the Filter window we need to set it here.
            //setAladinFOV( fov );
            const posAndFOV: PositionAndFOV = {
              fov: fov,
            };
            const newEvent: Event = {
              eventTarget: EventTarget.SEARCH_CATALOG_FORM,
              eventType: EventType.UPDATE_SEARCH_RA_DEC,
              parameters: posAndFOV,
            };
            dispatch(
              handleEvent({
                event: newEvent,
              }),
            );
            //
          }
          //
        }

        // clear the highlighted object, and the selected object.
        dispatch(setHighlightedID(''));
        dispatch(setSelectedIDs([]));
      }

    // if we click to close a results tab then remove this id from the list.
    if (button.name.length > 15)
      if (button.name.slice(0, 15) === 'btnCloseResults') {
        let id = button.name.slice(15 - button.name.length);

        // stop the event propogating to the tab itself.
        event.stopPropagation();

        // close this search results tab.
        dispatch(closeSearchResultsTab(id));
      }
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	close the context menus when the user clicks anywhere
  //	in the container.
  //
  //	------------------------------------------------------------

  function onClickDivHandler(event: React.MouseEvent<HTMLDivElement>) {
    //
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS,
        visible: false,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM,
        visible: false,
      }),
    );
    dispatch(setContextMenuRefreshStageData(Date.now()));
    //
  } // onClickDivHandler

  //	-------------------------------------------------
  //
  //	function that handles an update to the search
  //	position ra or dec on the search catalog form.
  //
  //	-------------------------------------------------

  function updateSearchPosition(args: {
    position?: {
      ra: number;
      dec: number;
    };
    fov?: number;
  }) {
    // set the Aladin position.
    if (sAladin !== null && args.position !== undefined)
      sAladin.gotoRaDec(args.position.ra, args.position.dec);

    // set the Aladin FOV.
    if (sAladin !== null && args.fov !== undefined) sAladin.setFov(args.fov);
  } // updateSearchPosition

  //	------------------------------------------------------------
  //
  //	run a new search based upon a footprint in Aladin.
  //
  //	------------------------------------------------------------

  function viewDataFromAladinFootprint(args: {
    position: { id: string; ra: number; dec: number }[];
  }) {
    //
    if (args.position.length > 0) {
      // find this ID within the array of results tabs.
      var index: number = -1;
      for (var i = 0; i < sSearchResults.length; i++) {
        var match: boolean = true;
        if (
          sSearchResults[i].obs_publisher_did.length === args.position.length &&
          args.position.length > 0
        ) {
          for (var j = 0; j < args.position.length; j++)
            if (sSearchResults[i].obs_publisher_did[j] !== args.position[j].id)
              match = false;
        } else match = false;

        // did we find a tab with this set of IDs ?
        if (match === true) index = i;
      }

      // if we didn't find it then we need to add it.
      if (index === -1) {
        //
        // compute average RA and DEC, and build list of IDs.
        var ra: number | undefined = 0.0;
        var dec: number | undefined = 0.0;
        var id: string[] = [];
        for (var i = 0; i < args.position.length; i++) {
          ra = ra + args.position[i].ra;
          dec = dec + args.position[i].dec;
          id.push(args.position[i].id);
        }
        if (args.position.length > 0) {
          ra = ra / args.position.length;
          dec = dec / args.position.length;
        }

        const newQuery: QueryParameters = {
          id: '',
          ra: ra,
          dec: dec,
          fov: 10.0,
          obs_publisher_did: id,
        };
        addSearchResultsTab({ query: newQuery });
        //
      } else if (sSearchCatalogTab !== sSearchResults[index].id) {
        //
        // if we DID find it then we need to make sure that this tab is highlighted.
        if (sSearchResults[index].id !== undefined) {
          dispatch(setSearchCatalogTab(sSearchResults[index].id));
        }
        //
      }
    }
    //
  } // viewDataFromAladinFootprint

  //	-------------------------------------------------
  //
  //	E V E N T S
  //
  //	-------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	build the list of Rucio locations for the selected files.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sSelectedRows hook fires [search-catalog]');

    fnD_buildRucioLocations({
      items: sSelectedRows.map((item) => {
        return {
          namespace: item.namespace,
          filename: item.filename,
        };
      }),
      catalog: 'search-catalog',
    });
    //
  }, [sSelectedRows]);

  //	------------------------------------------------------------
  //
  //	re-run the search-catalog query if the user has changed
  //	the data-product type filter.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    let reRunQuery: boolean = false;
    if (sDisplayedPage !== undefined) {
      //
      // has the filter, page, or page size on the current tab changed?
      reRunQuery =
        reRunQuery ||
        sDisplayedPage.dptFilter !== sDPTFilter ||
        sDisplayedPage.page !== sPage ||
        sDisplayedPage.pageSize !== sPageSize;
      //
    }

    // re-run the query.
    if (reRunQuery === true) {
      //
      // get the query for this tab.
      const query: QueryParameters | undefined = sSearchResults.find(
        (searchResults) => searchResults.id === sSearchCatalogTab,
      );

      // re-run the query.
      if (query !== undefined) {
        dispatch(getTapData({ query: query }));
      }
      //
    }
    //
  }, [sDPTFilter, sPage, sPageSize]);

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  return (
    <div
      ref={dataWindow}
      id="search-catalog-whole-window"
      style={{
        height: '100%',
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'row',
        padding: '0px 5px 0px 0px',
        position: 'relative',
      }}
      onClick={onClickDivHandler}
    >
      <div className="left-panel">
        {/*
        ///  the new-search panel
        */}
        <SearchCatalogForm
          addSearchResultsTab={addSearchResultsTab}
          updateSearchPosition={updateSearchPosition}
          project={sG_Project}
        />
        <div style={{ flex: '0 0 10px' }} />

        {/*
        ///  the aladin-lite side-panel
        */}
        <SidePanel
          position={SidePanelPosition.LEFT}
          title={t('Aladin Lite')}
          maximised={sAladinMaximised}
          redux={true}
          setMaximised={setAladinMaximised}
        />
      </div>
      <div
        style={{
          /*flex: '1 1 auto',*/
          display: 'flex',
          flexDirection: 'column',
          /*minWidth: '0',*/
          flex: '1 1',
          height: '100%',
          width: '0',
          padding: '0px 10px 0px 10px',
        }}
      >
        {/*
        ///  the main section, which contains the AladinLite window at the top (if maximised), and the query
        ///  results on the bottom
        */}
        <div
          className="aladin-section flex-shrink-0"
          style={{
            display:
              sLoadingAladin === true ||
              sAladinMaximised === false ||
              sSearchCatalogTab === 'selectedItems'
                ? 'none'
                : 'flex',
          }}
        >
          <div
            className="panel-main aladin-panel d-flex"
            role="region"
            aria-label="Aladin lite window"
            style={{ padding: '10px 10px 10px 10px' }}
            data-border="T"
          >
            <Aladin
              divElementID="aladin-lite-div-search-data"
              aladin={sAladin}
              setAladin={setAladin}
              selectedIDs={sSelectedIDs}
              setSelectedIDs={setSelectedIDs}
              highlightedID={sHighlightedID}
              setHighlightedID={setHighlightedID}
              setLoadingComponent={setLoadingAladin}
              updateSearchCatalogPosition={true}
              running={true}
            />
            {/* Vertical control rail */}
            <div className="aladin-rail" style={{ margin: '0px 0px 0px 5px' }}>
              <button
                className="minimise"
                name="minimiseAladin"
                type="button"
                title="Minimise Aladin"
                onClick={onClickHandler}
              >
                &laquo;
              </button>
              <div style={{ flex: '1 1' }} />
              <button
                name="searchLocation"
                type="button"
                title="Search at location"
                className="aladin-rail-btn"
                onClick={onClickHandler}
              >
                <img
                  src={searchLocationIcon}
                  role="decoration"
                  alt=""
                  width="30"
                  height="30"
                />
              </button>
              <div style={{ flex: '0 0 10px' }} />
              <button
                name="searchSelectedObject"
                type="button"
                title="Search selected object(s)"
                className="aladin-rail-btn"
                onClick={sSelectedIDs.length > 0 ? onClickHandler : undefined}
              >
                <img
                  src={
                    sSelectedIDs.length > 0
                      ? searchCatalogIcon
                      : searchCatalogDisabledIcon
                  }
                  role="decoration"
                  alt=""
                  width="30"
                  height="30"
                />
              </button>
            </div>
          </div>
        </div>
        <div
          role="region"
          aria-label="Search results"
          className="panel-main" // flex-grow-1 min-h-0 d-flex flex-column"
          style={{
            flex: '3 3',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/*
          ///  we have a header row containing the tabs for the search form and (multiple) results
          */}
          <div
            role="navigation"
            aria-label="Search results tabs"
            style={{ flex: '0 0 auto' }}
          >
            <TabBar
              searchCatalogTab={sSearchCatalogTab}
              onClickHandler={onClickHandler}
              tabDropdownDisplayed={sTabDropdownDisplayed}
              selectedRowsCount={sD_rucioLocations.length}
            />
          </div>

          {/*
          ///  display all the results tab pages (only one will be visible at any one time)
          */}
          <div
            className="flex-grow-1"
            style={{
              minHeight: 0,
            }}
          >
            {sSearchResults.map((query) => (
              <SearchCatalogResults
                key={'tab_' + query.id}
                query={query}
                launchService={launchService}
                hidden={sSearchCatalogTab !== query.id}
                tabID={query.id}
              />
            ))}
            <SelectedItemsTab
              hidden={sSearchCatalogTab !== 'selected-items'}
              launchService={launchService}
            />
          </div>
        </div>
      </div>
      <div
        className="right-panel"
        style={{ display: sD_rucioLocations.length > 0 ? 'block' : 'none' }}
      >
        {/* tools for creating or adding to a data collection */}
        <SelectedItemsPanel />
      </div>
      <ContextMenu
        key={'form_' + sContextMenuRefreshStageData.toString()}
        system={CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM}
      />
      <ContextMenu
        key={'staging_' + sContextMenuRefreshStageData.toString()}
        system={CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS}
      />
    </div>
  );
  //
} // SearchCatalog

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

// ------------------------------------------------------------
//
//	This dropdown mimics a droplist listbox, but
//	allows an image to be displayed next to each list item.
//
// ------------------------------------------------------------

function TabDropdownMenu(args: {
  sTabDropdownDisplayed: boolean;
  firstItem: number;
  lastItem: number;
  buttonHandler: any;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // LOCAL STATE VARIABLES:
  const sSearchResults: QueryParameters[] = useAppSelector(
    (state) => state.searchCatalog.searchResults,
  );

  // function that monitors for mouse clicks. we need to add {ref} to the DIV element of the dropdown menu.
  const { ref } = HideDropdownMenu({
    sDropdownDisplayed: args.sTabDropdownDisplayed,
    setDropdownDisplayed: setTabDropdownDisplayed,
    redux: true,
  });

  //	------------------------------------------------------------
  //
  //	A HTML component that renders a single menu item on the
  //	dropdown menu.
  //
  //	------------------------------------------------------------

  function MenuItem(args: { name: string; text: string; closeid: string; onClick: any }) {
    return (
      <button name={args.name} className="dropdown-list-item" onClick={args.onClick}>
        <div className="dropdown-list-item-text" data-align="L">
          {args.text}
        </div>
        <button
          name={args.closeid}
          type="button"
          className="button-close"
          onClick={args.onClick}
        >
          <div className="button-close-icon-container">
            <img src={closeIcon} role="decoration" alt="" width="20" height="20" />
          </div>
        </button>
      </button>
    );
  } // MenuItem

  return (
    <div
      ref={ref}
      style={{
        display: args.sTabDropdownDisplayed === true ? 'block' : 'none',
        position: 'absolute',
        top: '100%',
        left: '0%',
        border: 'none',
        zIndex: '9',
      }}
    >
      <div className="language-dropdown-menu">
        {sSearchResults
          .filter((item, index) => index < args.firstItem || index > args.lastItem)
          .map((item, index) =>
            MenuItem({
              name: 'btnResults' + item.id,
              text: 'Results ' + item.id,
              closeid: 'btnCloseResults' + item.id,
              onClick: args.buttonHandler,
            }),
          )}
      </div>
    </div>
  );
} // TabDropdownMenu

// ------------------------------------------------------------
//
//	Display the search results tabs at the top of the
//	screen.
//
// ------------------------------------------------------------

function TabBar(args: {
  searchCatalogTab: string;
  onClickHandler: any;
  tabDropdownDisplayed: boolean;
  selectedRowsCount: number;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // LOCAL STATE VARIABLES:
  const sSearchResults: QueryParameters[] = useAppSelector(
    (state) => state.searchCatalog.searchResults,
  );
  const sMaxTabs: number = useAppSelector((state) => state.searchCatalog.maxTabs);
  const sTabWidth: number = useAppSelector((state) => state.searchCatalog.tabWidth);
  const sDropdownWidth: number = useAppSelector(
    (state) => state.searchCatalog.dropdownWidth,
  );
  const sFirstTab: number = useAppSelector((state) => state.searchCatalog.firstTab);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // handle changes in size to the tab bar.
  const resizeTabBar = React.useRef<ResizeObserver>(
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      //
      // recalculate the number of tabs that we can display along the top. any other tabs
      // must be placed in a drop-down menu.
      if (entries[0].contentRect.width > 0) {
        //
        const SPACING_WIDTH: number = 20.0;
        const MIN_DROPDOWN_WIDTH: number = 40.0;
        const MIN_TAB_WIDTH: number = 191.0;
        const SELECTED_ITEMS_TAB_WIDTH: number = 160.0;
        const USABLE_WIDTH: number =
          entries[0].contentRect.width - SPACING_WIDTH - SELECTED_ITEMS_TAB_WIDTH;

        // calculate the maximum number of tabs we can fit across this tab bar.
        var maxTabs: number = Math.floor(
          (USABLE_WIDTH - MIN_DROPDOWN_WIDTH) / MIN_TAB_WIDTH,
        );

        // calculate the tab width.
        const tabWidth: number = Math.floor(
          (USABLE_WIDTH - MIN_DROPDOWN_WIDTH) / maxTabs,
        );

        // let the dropdown take up any rounding error.
        const dropdownWidth: number = USABLE_WIDTH - maxTabs * tabWidth;

        // set the state variables to hold the number of tabs and the tab width.
        dispatch(setMaxTabs(maxTabs));
        dispatch(setTabWidth(tabWidth));
        dispatch(setDropdownWidth(dropdownWidth));
        //
      }
      //
    }),
  );

  // create a reference to the HTML Div element that contains the tab bar, so that we can monitor for changes in size.
  const tabButtons = React.useCallback(
    (container: HTMLDivElement) => {
      //
      // check that this reference has actually been assigned to an element, and switch on observing.
      if (container !== null) {
        resizeTabBar.current.observe(container);
      }

      // When element is unmounted, ref callback is called with a null argument
      // => best time to cleanup the observer
      else if (resizeTabBar.current) {
        resizeTabBar.current.disconnect();
      }
      //
    },
    [resizeTabBar.current],
  );

  // check if we need to hide the drop-down menu.
  if (args.tabDropdownDisplayed === true && sMaxTabs >= sSearchResults.length) {
    dispatch(setTabDropdownDisplayed(false));
  }

  // change the first tab displayed in the list to ensure that the selected tab is displayed.
  {
    let index = sSearchResults.findIndex(
      (element) => element.id === args.searchCatalogTab,
    );
    var firstTab: number = sFirstTab;
    if (index > -1) {
      if (index < firstTab) firstTab = index;
      if (index >= firstTab + sMaxTabs) firstTab = index - sMaxTabs + 1;
    }
    if (sSearchResults.length - firstTab - 1 < sMaxTabs)
      firstTab = sSearchResults.length - sMaxTabs;
    if (firstTab < 0) firstTab = 0;
    if (firstTab != sFirstTab) {
      dispatch(setFirstTab(firstTab));
    }
  }

  return (
    /* we have a header row containing the tabs for the search form and (multiple) results */
    <div ref={tabButtons} className="search-tab-bar">
      <div style={{ flex: '0 0 10px' }} />

      {/* first we have the dropdown button for displayed extra tabs */}
      <ToolSmall
        name="btnTabDropdown"
        text="&#9662;"
        onClick={args.onClickHandler}
        left={true}
        displayDropdown={true}
        tabWidth={sDropdownWidth}
        disabled={sMaxTabs >= sSearchResults.length || sMaxTabs === -1}
        dropdown={
          <TabDropdownMenu
            sTabDropdownDisplayed={args.tabDropdownDisplayed}
            firstItem={sFirstTab}
            lastItem={sFirstTab + sMaxTabs - 1}
            buttonHandler={args.onClickHandler}
          />
        }
      />

      {/* and then we have the results tabs */}
      {sSearchResults
        .filter((tab, index) => index >= sFirstTab && index <= sFirstTab + sMaxTabs - 1)
        .map((tab, index) => (
          <ToolSmall
            key={tab.id}
            name={'btnResults' + tab.id}
            closeid={'btnCloseResults' + tab.id}
            text={t('Results', { number: tab.id })}
            withClose={true}
            onClick={args.onClickHandler}
            selected={args.searchCatalogTab === tab.id}
            left={false}
            tabWidth={sTabWidth}
          />
        ))}

      {/* and then we have the empty, expanding tab in the middle */}
      <ToolSmall
        name=""
        text=""
        onClick={args.onClickHandler}
        empty={true}
        left={false}
        hidden={sMaxTabs <= sSearchResults.length}
      />

      {/* and then we have the selected items tab */}
      <ToolSmall
        name="btnSelectedItems"
        text={t('Selected items')}
        withNumber={true}
        count={args.selectedRowsCount}
        onClick={args.onClickHandler}
        selected={args.searchCatalogTab === 'selected-items'}
        right={true}
        disabled={sSearchResults.length > 0 ? false : true}
      />

      <div style={{ flex: '0 0 10px' }} />
    </div>
  );
} // TabBar
